import clsx from 'clsx';
import { isEmpty } from 'lodash';
import { func, node, string } from 'prop-types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { createSearchParams, generatePath, useNavigate } from 'react-router-dom';

import { Card, ExpirationTimer, Icon, Loader, Skeleton, useCartContext } from 'components';
import { formatLocalizedCurrency } from 'helpers';
import { useFetch } from 'hooks';
import { routes } from 'router';
import { appService } from 'services';
import { setShowCart } from 'store';

import 'assets/styles/components/cart.scss';

const CartSkeleton = () => {
  return (
    <>
      <div className="flex-grow mt-8">
        {Array.from({ length: 4 }).map((_, index) => (
          <div key={`card-skeleton-${index}`} className="rounded-xl py-1">
            <Skeleton classNames="h-12 w-full" />
          </div>
        ))}
      </div>

      <div className="flex justify-center">
        <Skeleton classNames="h-10 w-2/5 rounded-full" />
      </div>

      <div className="w-full flex flex-col pb-3 lg:pb-0 gap-2 border-t border-gray-300 pt-4">
        {Array.from({ length: 3 }).map((_, index) => (
          <div key={`card-skeleton-${index}`} className="rounded-xl">
            <Skeleton classNames="h-8 w-full" />
          </div>
        ))}
        <Skeleton classNames="h-10 w-full rounded-full" />
      </div>
    </>
  );
};

const DeleteCartButton = ({ className = '' }) => {
  const { t } = useTranslation();

  const { cart, deleteTotalCart, deleteLoading, deleteTotalCartLoading } = useCartContext();

  return (
    <>
      {!isEmpty(cart?.lines) && (
        <button
          type="button"
          className={clsx('text-sm flex gap-2', className)}
          onClick={() => deleteTotalCart(true)}
          disabled={deleteLoading || deleteTotalCartLoading}
          data-test-id="deleteTotalCart"
        >
          {deleteLoading || deleteTotalCartLoading ? (
            <Loader loaderClass="w-4 h-4 shrink-0" />
          ) : (
            <Icon name="xmark" classes="h-4 shrink-0" />
          )}
          {t('cart:deleteCart')}
        </button>
      )}
    </>
  );
};

const Cart = ({ onClose }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { cartLoading, cart, deleteOneOrder, deleteLoading, deleteTotalCartLoading } = useCartContext();

  const { call: sportCategoriesCall, loading: sportCategoriesLoading } = useFetch({ withSetState: false });

  const cartItemsPrices = useMemo(() => {
    const totalPrice = () => {
      const total = cart?.lines?.reduce((accumulator, line) => {
        return accumulator + line.price;
      }, 0);

      return appService.isTouloisirs() ? total || 0 : cart?.priceWithoutShipping;
    };

    const totalPriceValue = totalPrice();
    const subsidyValue = cart?.discountAmount || cart?.subsidizedAmount || 0;
    const subTotalValue = totalPriceValue - subsidyValue;

    const subventionKey = appService.isTouloisirs() ? 'subsidy' : 'subvention';

    const items = [
      { i18nKey: 'totalVAT', value: totalPriceValue / 100 },
      { i18nKey: subventionKey, value: subsidyValue / 100 },
      { i18nKey: 'subTotal', value: subTotalValue / 100 }
    ];

    let filteredItems = items.filter((item) => item.value > 0 || item.i18nKey !== subventionKey);
    filteredItems = filteredItems.map((item) => ({
      ...item,
      value: ['subsidy', 'subvention'].includes(item.i18nKey)
        ? `- ${formatLocalizedCurrency(item.value)}`
        : formatLocalizedCurrency(item.value)
    }));

    return filteredItems;
  }, [cart]);

  const goToProductShow = async (product) => {
    if (
      !product?.productId ||
      !product?.typeItem ||
      sportCategoriesLoading ||
      deleteLoading ||
      deleteTotalCartLoading
    ) {
      return;
    }

    const { data } =
      (await sportCategoriesCall({
        service: 'castelis',
        path: '/repository/sportWellnessCategory',
        method: 'get'
      })) || {};

    const sportCategories = (data?.body || []).flatMap((category) => [
      category.id,
      ...category.subCategories.map((subCategory) => subCategory.id)
    ]);

    onClose();
    let path = sportCategories.includes(product?.subsidizedInfo?.categoryId)
      ? routes.sportShow.path
      : routes.leisureShow.path;

    let productId = product.productId;

    if (product?.typeItem === 'BUYBOX') {
      path = routes.giftcardShow.path;
      productId = product?.options?.merchantId;
    }

    const productPath = generatePath(path, { id: productId });
    navigate({ pathname: productPath, search: createSearchParams({ typeItem: product.typeItem }).toString() });
  };

  const validateCart = () => {
    dispatch(setShowCart(false));
    navigate(routes.leisurePurchase.children.summary.path);
  };

  return (
    <div className="cart-content cart-container-height px-8 lg:py-6">
      <button type="button" className="lg:hidden self-start" onClick={onClose}>
        <Icon name="angle-left" classes="text-gray-500" />
        <span className="ml-3">{t('buttons.goBack')}</span>
      </button>

      <div className="flex justify-between">
        <h2 className="h2 underlined-blue">{t('cart:yourCart')}</h2>

        <ExpirationTimer />

        <button type="button" className="hidden lg:block" onClick={onClose} data-test-id="closeCartButton">
          <Icon name="xmark" classes="text-gray-500 h-6 " />
        </button>
      </div>

      {(cartLoading || deleteLoading) && <CartSkeleton />}

      {!cartLoading && !deleteLoading && (
        <>
          <div className="relative flex-grow overflow-y-auto mt-6">
            {isEmpty(cart?.lines) ? (
              <div className="flex flex-col items-center gap-4">
                <img
                  src={appService.loadBrandImage('emptyCart')}
                  alt=""
                  width="320"
                  height="200"
                  className="w-2/3 md:w-1/2 lg:w-full object-cover"
                  data-test-id="emptyCartImage"
                />
                <p className="uppercase text-sm">{t('cart:emptyCart')}</p>
                <p>{t('cart:add')}</p>
              </div>
            ) : (
              <div className="bg-gray-100 text-gray-700 rounded-lg font-bold px-4 py-3 grid grid-cols-12 gap-1">
                <p className="col-span-6">{t('cart:label')}</p>
                <p className="col-span-3">{t('cart:price')}</p>
                <p className="col-span-2">{t('quantity')}</p>
              </div>
            )}

            {cart?.lines?.map((order, index) => (
              <Card
                key={`cart-order-${index}`}
                className={clsx('my-4 shadow-none border !p-0 overflow-hidden', {
                  'opacity-50': sportCategoriesLoading,
                  'cursor-pointer hover:border-primary-500': order?.typeItem
                })}
              >
                <div className="grid grid-cols-12 gap-3 items-center p-4 relative">
                  <button
                    type="button"
                    className="absolute inset-0 opacity-0"
                    onClick={() => goToProductShow(order)}
                    data-test-id="productCart"
                  >
                    {t('cart:seeProductDetail', { productTitle: order?.title || order?.label })}
                  </button>

                  <h2 className="capitalize col-span-6 truncate-line two-lines">{order?.title || order?.label}</h2>
                  <p className="col-span-3">{formatLocalizedCurrency(order.unitPrice / 100)}</p>
                  <p className="col-span-2">{order.quantity}</p>
                  <button
                    type="button"
                    onClick={() => deleteOneOrder(order)}
                    className="col-span-1 self-center relative z-10"
                    data-test-id="deleteOneOrder"
                  >
                    <Icon name="trash" classes="text-gray-500 hover:text-yellow-500" />
                  </button>
                </div>
              </Card>
            ))}

            {sportCategoriesLoading && (
              <div className="absolute inset-0 z-20 flex items-center justify-center">
                <Loader loaderClass="w-5 h-5 text-primary-50 shrink-0" />
              </div>
            )}
          </div>

          <DeleteCartButton className="btn-cart" />

          <div className="w-full uppercase flex flex-col pb-4 lg:pb-0 gap-4 border-t border-gray-300 pt-4">
            {cartItemsPrices.map((item, i) => (
              <div className="flex justify-between" key={`cart-price-item-${i}`}>
                <p className="font-semibold">{t(`cart:${item.i18nKey}`)}</p>
                <p data-test-id={`totalCartPrice-${item.i18nKey}`}>{item.value}</p>
              </div>
            ))}

            <button
              type="button"
              className="btn small secondary md:w-fit md:mx-auto"
              disabled={isEmpty(cart?.lines) || deleteLoading}
              onClick={validateCart}
            >
              {t('cart:submitCart')}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

DeleteCartButton.propTypes = {
  className: string,
  children: node
};

Cart.propTypes = {
  onClose: func.isRequired
};

export default Cart;
